import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@/lib/server-fn-shim";
import { getMyTrip } from "@/lib/trip.functions";
import { TripSwitcher } from "@/components/TripSwitcher";
import { getMyMedia } from "@/lib/media.functions";
import { MediaPreviewDrawer, type PreviewItem } from "@/components/MediaPreviewDrawer";
import { Image as ImageIcon, RefreshCw, Settings2, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { RoavaPage } from "@/components/roava";
import { Button } from "@/components/ui/button";
import { useUploadActions } from "@/lib/upload/use-upload-actions";
import { SyncOutboxCard } from "@/components/SyncOutboxCard";
import { ScanUploadProgressList } from "@/components/upload/ScanUploadProgressList";
import { UploadStatusChips } from "@/components/upload/UploadStatusChips";
import { LibraryCacheScope } from "@/components/library/LibraryCacheScope";
import { MediaTimeline } from "@/components/library/MediaTimeline";
import { FixStuckUploadsSheet } from "@/components/FixStuckUploadsSheet";
import { useAuth } from "@/lib/auth";
import { useRetryUpload } from "@/lib/upload/use-retry-upload";
import { isRetryable } from "@/lib/upload/retry";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";

type MediaFilter = "all" | "photo" | "video";

export const Route = createFileRoute("/_authenticated/my-uploads")({
  head: () => pageMeta({ title: 'My Uploads — Roava by WGP Apps', description: "View and manage the photos and videos you've contributed to the trip.", path: '/my-uploads' }),
  component: MyUploads,
});

function MyUploads() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const uploadActions = useUploadActions();
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;
  const tripFn = useServerFn(getMyTrip);
  const mediaFn = useServerFn(getMyMedia);
  const { data: tripData } = useQuery({ queryKey: ["my-trip"], queryFn: () => tripFn({}) });
  const tripId = tripData?.trip?.id;

  const { data, isLoading } = useQuery({
    queryKey: ["my-media", tripId],
    queryFn: () => mediaFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });

  const allItems = (data?.items ?? []);
  const uploaded = allItems.filter((x: any) => x.upload_status === "uploaded");
  const failedItems = allItems.filter((x: any) => x.upload_status === "failed");
  const failedCount = failedItems.length;
  const photoCount = uploaded.filter((x: any) => x.media_type === "photo").length;
  const videoCount = uploaded.filter((x: any) => x.media_type === "video").length;
  const [filter, setFilter] = useState<MediaFilter>("all");
  const filtered = useMemo(
    () => filter === "all" ? uploaded : uploaded.filter((x: any) => x.media_type === filter),
    [uploaded, filter]
  );
  const [selected, setSelected] = useState<PreviewItem | null>(null);
  const [fixStuckOpen, setFixStuckOpen] = useState(false);

  const chips: { id: MediaFilter; label: string }[] = [
    { id: "all", label: uploaded.length ? t("my_uploads.filter_all_n", { n: uploaded.length }) : t("my_uploads.filter_all") },
    { id: "photo", label: t("my_uploads.filter_photos", { n: photoCount }) },
    { id: "video", label: t("my_uploads.filter_videos", { n: videoCount }) },
  ];

  return (
    <LibraryCacheScope userId={currentUserId} tripId={tripId ?? null}>
      <RoavaPage

        title={t("my_uploads.title")}
        subtitle={
          isLoading && !data
            ? t("my_uploads.loading")
            : t(uploaded.length === 1 ? "my_uploads.synced_one" : "my_uploads.synced_other", { n: uploaded.length })
        }
        action={<TripSwitcher />}
      >
        <div className="mb-3">
          <SyncOutboxCard />
          <ScanUploadProgressList />
          <div className="mt-2">
            <UploadStatusChips />
          </div>
        </div>
        {failedCount > 0 && (
          <div className="mb-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-3 flex items-start gap-3">
            <AlertTriangle className="size-5 text-destructive shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-destructive">
                {t("my_uploads.needs_attention", { n: failedCount })}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t(failedCount === 1 ? "my_uploads.failed_body_one" : "my_uploads.failed_body_other", { n: failedCount })}
              </p>
            </div>
            <Button
              size="sm"
              variant="destructive"
              className="h-8 text-xs shrink-0"
              onClick={() => setFixStuckOpen(true)}
            >
              {t("my_uploads.fix_now")}
            </Button>
          </div>
        )}
        {failedCount > 0 && (
          <FailedUploadsList items={failedItems} tripId={tripId ?? null} />
        )}

        {uploaded.length > 0 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar py-1 -mx-1 px-1 mb-4">
            {chips.map((c) => {
              const active = filter === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setFilter(c.id)}
                  className={cn(
                    "px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-colors border",
                    active
                      ? "bg-[var(--roava-blue)] text-[var(--roava-blue-foreground)] border-[var(--roava-blue)]"
                      : "bg-[var(--roava-card)] text-[var(--roava-text)] border-[var(--roava-border)] hover:bg-[var(--roava-gray)]/50",
                  )}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        )}

          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border p-6 text-center mt-4">
              {uploaded.length === 0 ? t("my_uploads.empty_none") : t("my_uploads.empty_filter")}
            </p>
          ) : (
            <div className="mt-4">
              <MediaTimeline items={filtered} onSelect={(it) => setSelected(it as PreviewItem)} />
            </div>
        )}
        {/* Spacer so the last day-section isn't hidden behind the sticky upload card. */}
        <div aria-hidden className="h-32" />
      </RoavaPage>


      {/* Sticky upload-actions card — sits above the floating BottomTabs pill.
          Both buttons always render; the one matching the saved upload mode is
          the filled primary, the other is the outline secondary. */}
      <div
        className="fixed inset-x-0 z-40 px-4 pointer-events-none"
        style={{
          bottom:
            "calc(var(--bottom-nav-height, 76px) + env(safe-area-inset-bottom) + 8px)",
        }}
      >
        <div className="mx-auto max-w-[480px] pointer-events-auto">
          <div className="rounded-2xl border border-border bg-background/85 backdrop-blur-md shadow-lg p-3">
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => void uploadActions.scanTripMedia()}
                variant={uploadActions.mode === "auto" ? "default" : "outline"}
                className="h-11 rounded-xl font-semibold text-xs px-2"
              >
                <RefreshCw className="size-4 mr-1.5" />
                {t("my_uploads.scan_trip_media")}
              </Button>
              <Button
                onClick={() => void uploadActions.pickFromPhotos()}
                variant={uploadActions.mode === "manual" ? "default" : "outline"}
                className="h-11 rounded-xl font-semibold text-xs px-2"
              >
                <ImageIcon className="size-4 mr-1.5" />
                {t("my_uploads.pick_from_photos")}
              </Button>
            </div>
            <button
              type="button"
              onClick={() => nav({ to: "/upload-mode" })}
              className="mt-2 w-full inline-flex items-center justify-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
            >
              <Settings2 className="size-3" />
              {t("my_uploads.change_settings")}
            </button>
          </div>
        </div>
      </div>

      {uploadActions.webFileInput}
      {uploadActions.reviewSheet}
      {uploadActions.gateDialogs}

      <MediaPreviewDrawer item={selected} onClose={() => setSelected(null)} />
      <FixStuckUploadsSheet
        open={fixStuckOpen}
        onOpenChange={setFixStuckOpen}
        tripId={tripId ?? null}
        pending={failedCount}
      />
    </LibraryCacheScope>
  );
}

function FailedUploadsList({ items, tripId }: { items: any[]; tripId: string | null }) {
  const { t } = useTranslation();
  const retryMut = useRetryUpload(tripId);
  const [retryable, setRetryable] = useState<Record<string, boolean>>({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        items.map(async (it) => [it.id, await isRetryable(it.id)] as const),
      );
      if (!cancelled) setRetryable(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [items]);

  return (
    <ul className="mb-4 grid gap-2">
      {items.map((it) => {
        const canRetry = retryable[it.id] !== false;
        const pending = retryMut.isPending && retryMut.variables === it.id;
        const when = it.captured_at ?? it.uploaded_at ?? it.created_at;
        return (
          <li
            key={it.id}
            className="flex items-center gap-3 rounded-xl border border-border bg-card/50 p-2.5"
          >
            <div className="size-10 rounded-lg bg-muted grid place-items-center shrink-0">
              <ImageIcon className="size-4 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">
                {it.original_filename ?? t("my_uploads.photo_fallback", { id: String(it.id).slice(0, 6) })}
              </p>
              <p className="text-[11px] text-muted-foreground truncate">
                {when ? formatDistanceToNow(new Date(when), { addSuffix: true }) : t("my_uploads.unknown_time")}
                {it.last_error ? ` · ${it.last_error}` : ""}
              </p>
              {!canRetry && (
                <p className="text-[11px] text-amber-600 mt-0.5">
                  {t("my_uploads.reference_lost")}
                </p>
              )}
            </div>
            <Button
              size="sm"
              variant={canRetry ? "destructive" : "outline"}
              className="h-8 text-xs shrink-0"
              disabled={!canRetry || pending}
              onClick={() => retryMut.mutate(it.id)}
            >
              <RefreshCw className={cn("size-3.5 mr-1", pending && "animate-spin")} />
              {t("my_uploads.retry")}
            </Button>
          </li>
        );
      })}
    </ul>
  );
}


